/** Reading what a waiting pull request would publish, for the person who has
 *  to decide whether it should.
 *
 * The gate hands an admin numbers — "perimeter: right 96 %, 4 gaps",
 * "crossing spread 14 m" — and the numbers are true and still do not say
 * whether to merge. Four gaps of three metres at the pit entry is a survey
 * that is finished; one gap of a hundred and twenty is a corner nobody drove.
 * Fourteen metres across the road is two cars on different lines; fourteen
 * metres along it is a finish line in two places. The map answers those, but
 * only if it can be taken to the place, so this module turns the candidate
 * into a list of places: each gap with its length and where it is, and the
 * finish crossings with how far apart they are.
 *
 * It also sets the candidate against what is published today, per side, which
 * is the update gate's question — "did coverage go down" — asked as a figure
 * instead of a verdict.
 *
 * All of it is arithmetic on the document, kept out of the component so it
 * can be tested by feeding it numbers.
 */

import { pathFromPoints, runToPoints } from '@/components/TrackOutline';
import type { GroundPoint, Segment, TrackGeometry } from '@/lib/types';

/** The issues that are about a pull request left waiting. Each names a
 *  circuit, and the job sends that circuit's candidate whenever it leaves a
 *  pull request open, so these are the cards that can draw one. */
export const WAITING_PR_ISSUES: readonly string[] = [
  'gate_failed',
  'coverage_regressed',
  'pr_check_failed',
];

export function showsCandidate(issue: { type: string; official_id: string }): boolean {
  return issue.official_id !== '' && WAITING_PR_ISSUES.includes(issue.type);
}

export interface Gap {
  /** `L2`: the side, and which of that side's gaps, counted from one. */
  id: string;
  side: 'L' | 'R';
  length_m: number;
  /** Its middle, which is where the map goes. */
  at: GroundPoint;
}

function finiteSegment(segment: Segment | undefined): segment is Segment {
  return Array.isArray(segment) && segment.length >= 4 && segment.slice(0, 4).every(Number.isFinite);
}

/** Every gap on both sides, the longest first: the one most likely to be the
 *  reason the gate declined is the one to look at first. */
export function gapList(geometry: TrackGeometry): Gap[] {
  const gaps: Gap[] = [];
  for (const side of ['L', 'R'] as const) {
    (geometry.gaps?.[side] ?? []).forEach((segment, index) => {
      if (!finiteSegment(segment)) return;
      const [x1, z1, x2, z2] = segment;
      gaps.push({
        id: `${side}${index + 1}`,
        side,
        length_m: Math.hypot(x2 - x1, z2 - z1),
        at: { x: (x1 + x2) / 2, z: (z1 + z2) / 2 },
      });
    });
  }
  return gaps.sort((a, b) => b.length_m - a.length_m);
}

export const SIDE_NAME: Record<'L' | 'R', string> = { L: 'left', R: 'right' };

export interface CrossingSpread {
  count: number;
  /** The greatest distance between any two crossings — a diameter, which is
   *  how the gate measures it. */
  spread_m: number;
  /** The middle of them. */
  at: GroundPoint;
}

export function crossingSpread(points: readonly GroundPoint[]): CrossingSpread | null {
  if (points.length === 0) return null;
  let spread = 0;
  let x = 0;
  let z = 0;
  points.forEach((a, index) => {
    x += a.x;
    z += a.z;
    for (const b of points.slice(index + 1)) {
      spread = Math.max(spread, Math.hypot(a.x - b.x, a.z - b.z));
    }
  });
  return { count: points.length, spread_m: spread, at: { x: x / points.length, z: z / points.length } };
}

/**
 * How many metres across the map should show to look at something `length`
 * metres long: the thing itself and the road either side of it, and never so
 * close that a three-metre gap is a screenful of two lines.
 */
export function lookSpan(length: number): number {
  return Math.max(120, length * 4);
}

export interface SideChange {
  side: 'L' | 'R';
  now: number | null;
  /** Null for a circuit's first publication: there is nothing to compare. */
  was: number | null;
  /** Percentage points; negative is coverage the pull request would lose. */
  change: number | null;
}

/** Each side's coverage in the candidate against what is published. */
export function coverageChange(
  candidate: TrackGeometry,
  published: TrackGeometry | null,
): SideChange[] {
  return (['L', 'R'] as const).map((side) => {
    const now = candidate.coverage?.[side]?.pct ?? null;
    const was = published?.coverage?.[side]?.pct ?? null;
    return {
      side,
      now,
      was,
      change: now !== null && was !== null ? Number((now - was).toFixed(1)) : null,
    };
  });
}

// ── what is drawn over the map ────────────────────────────────────────────

/** The published borders as one path, to ghost behind the candidate. */
export function outlinePath(published: TrackGeometry | null): string {
  if (!published?.borders) return '';
  return [...(published.borders.L ?? []), ...(published.borders.R ?? [])]
    .map((run) => pathFromPoints(runToPoints(run)))
    .filter(Boolean)
    .join(' ');
}

/**
 * Positions as dots that stay the same size on screen. Each is a stroke one
 * centimetre long with a round cap and a non-scaling width, so the cap is the
 * dot and no zoom makes it bigger — the children of `TrackMap` are drawn in
 * world metres and have no other way to be a constant number of pixels.
 */
export function dotsPath(points: readonly GroundPoint[]): string {
  return points
    .filter((point) => Number.isFinite(point.x) && Number.isFinite(point.z))
    .map((point) => `M ${point.x.toFixed(2)} ${(-point.z).toFixed(2)} h 0.01`)
    .join(' ');
}
